import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Dimensions, Image } from 'react-native';
import { PressableCard } from './PressableCard';
import { SkeletonCard } from './SkeletonLoader';

const { width: screenWidth } = Dimensions.get('window');
import { apiService } from '../services/apiService';
import { ExpiryData } from '../types';
import { isXl } from '../utils/responsiveStyles';

interface ProductsNearExpiryProps {
  includeWarrants?: boolean;
  onExpiryPress?: (quarter: string) => void;
  ownershipScope?: 'group' | 'self';
}

export const ProductsNearExpiry: React.FC<ProductsNearExpiryProps> = React.memo(({
  includeWarrants = true,
  onExpiryPress,
  ownershipScope = 'group',
}) => {
  const [expiryData, setExpiryData] = useState<ExpiryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExpiryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getProductsNearExpiry(includeWarrants, ownershipScope);
      setExpiryData(data);
    } catch (err) {
      console.error('Error fetching expiry data:', err);
      setError('Error loading expiry data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpiryData();
  }, [includeWarrants, ownershipScope]);

  if (loading) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Products near expiry</Text>
        <SkeletonCard />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Products near expiry</Text>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </View>
    );
  }

  const isTabletOrDesktop = isXl(screenWidth) || screenWidth >= 1024;

  return (
    <View style={isTabletOrDesktop ? styles.sectionTablet : styles.section}>
      <Text style={styles.sectionTitle}>Products near expiry</Text>
      <View style={isTabletOrDesktop ? styles.expiryContainerTablet : styles.expiryContainer}>
        {expiryData.length > 0 ? (
          expiryData.map((item, index) => (
            isTabletOrDesktop ? (
              <PressableCard 
                key={item.id} 
                style={[
                  styles.expiryCardTablet,
                  index < expiryData.length - 1 ? styles.expiryCardTabletBorder : undefined
                ]}
                onPress={() => onExpiryPress?.(item.period)}
                pressedScale={0.98}
              >
                <View style={styles.iconContainer}>
                  <Image 
                    source={require('../../assets/icons/calendar-tablet.png')}
                    style={styles.calendarIconImage}
                    resizeMode="contain"
                  />
                </View>
                <View style={styles.expiryTextContainer}>
                  <Text style={styles.expiryCountTablet}>{item.count}</Text>
                  <Text style={styles.expiryPeriodTablet}>{item.period}</Text>
                </View>
              </PressableCard>
            ) : (
              <PressableCard 
                key={item.id} 
                style={index === expiryData.length - 1 ? 
                  {...styles.expiryCard, marginRight: 0} : 
                  styles.expiryCard
                }
                onPress={() => onExpiryPress?.(item.period)}
                pressedScale={0.95}
              >
                <Text style={styles.expiryCount}>{item.count}</Text>
                <Text style={styles.expiryPeriod}>{item.period}</Text>
              </PressableCard>
            )
          ))
        ) : (
          <>
            <PressableCard 
              style={styles.expiryCard}
              onPress={() => onExpiryPress?.('Current Quarter')}
              pressedScale={0.95}
            >
              <Text style={styles.expiryCount}>0</Text>
              <Text style={styles.expiryPeriod}>Current Quarter</Text>
            </PressableCard>
            <PressableCard 
              style={styles.expiryCard}
              onPress={() => onExpiryPress?.('Next Quarter')}
              pressedScale={0.95}
            >
              <Text style={styles.expiryCount}>0</Text>
              <Text style={styles.expiryPeriod}>Next Quarter</Text>
            </PressableCard>
            <PressableCard 
              style={{...styles.expiryCard, marginRight: 0}}
              onPress={() => onExpiryPress?.('Following Quarter')}
              pressedScale={0.95}
            >
              <Text style={styles.expiryCount}>0</Text>
              <Text style={styles.expiryPeriod}>Following Quarter</Text>
            </PressableCard>
          </>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  section: {
    marginBottom: 20,
    paddingHorizontal: screenWidth < 360 ? 12 : 16,
  },
  sectionTablet: {
    marginBottom: screenWidth >= 1280 ? 20 : 16,
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#F4F4F4',
    padding: screenWidth >= 1600 ? 24 : screenWidth >= 1280 ? 20 : 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: screenWidth >= 1600 ? 18 : screenWidth >= 1280 ? 16 : 15,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: screenWidth >= 1280 ? 16 : 12,
    fontFamily: 'Montserrat_700Bold',
  },
  expiryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 0,
  },
  expiryContainerTablet: {
    flexDirection: 'column',
    gap: 0,
  },
  expiryCard: {
    width: (screenWidth - (screenWidth < 360 ? 24 : 32) - 16) / 3 - 20, 
    minHeight: screenWidth < 360 ? 100 : 120,
    maxHeight: screenWidth < 360 ? 140 : 180,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: screenWidth < 360 ? 2 : 4,
    marginRight: 8, // Gap entre cards
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#F4F4F4',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  expiryCardLast: {
    marginRight: 0, // La última card no necesita margin
  },
  expiryItem: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  expiryCount: {
    fontSize: screenWidth < 360 ? 32 : 42, // Tamaño adaptativo
    fontWeight: '700',
    color: '#1F2937',
    lineHeight: screenWidth < 360 ? 36 : 48,
    marginBottom: screenWidth < 360 ? 4 : 6,
    fontFamily: 'Montserrat_700Bold',
  },
  expiryPeriod: {
    fontSize: screenWidth < 360 ? 10 : 12,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: screenWidth < 360 ? 14 : 16,
    fontFamily: 'Montserrat_400Regular',
  },
  expiryCardTablet: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 16,
    backgroundColor: '#fff',
    borderRadius: 0,
    borderWidth: 0,
    shadowColor: 'transparent',
    elevation: 0,
  },
  expiryCardTabletBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#E0F2FE',
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarIcon: {
    fontSize: 28,
  },
  calendarIconImage: {
    width: 32,
    height: 32,
  },
  expiryTextContainer: {
    flex: 1,
  },
  expiryCountTablet: {
    fontSize: 32,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
    fontFamily: 'Montserrat_700Bold',
  },
  expiryPeriodTablet: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  errorContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  noDataText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingVertical: 40,
    fontFamily: 'Montserrat_400Regular',
  },
});
